"use client";

/**
 * WebBrowserWrapper
 *
 * Wraps the Framer WebBrowserEnvironment component with real browsing
 * functionality. Supports two variants:
 *   - Variant 1 (webpage view): iframe + nav buttons
 *   - Variant 2 (history view): dynamic history entries from DB
 *
 * Approach: "Wrapper overlay" — the Framer component renders as-is.
 * MutationObserver watches for key DOM elements and attaches click handlers
 * or injects dynamic content.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useWebBrowser, type HistoryEntry } from "@/contexts/web-browser-context";
import ConfirmationRequest from "@/framer/confirmation-request.jsx";
import "./web-browser-overrides.css";

// ============================================================================
// Strings (extracted for future i18n)
// ============================================================================
const STRINGS = {
	loading: "Loading\u2026",
	copyLink: "Copy Link",
	openExternal: "Open in external browser",
	copied: "Copied!",
	noHistory: "No browsing history yet",
} as const;

// ============================================================================
// Lazy-load the Framer component (SSR disabled — it uses browser APIs)
// ============================================================================
const FramerWebBrowser = dynamic(
	() => import("@/framer/web-browser-environment.jsx"),
	{
		ssr: false,
		loading: () => (
			<div className="flex items-center justify-center" style={{ width: 393, height: 851 }}>
				<p>{STRINGS.loading}</p>
			</div>
		),
	},
);

// ============================================================================
// Helpers
// ============================================================================

/** Build the proxied URL for the iframe */
function getProxyUrl(targetUrl: string): string {
	return `/api/proxy?url=${encodeURIComponent(targetUrl)}`;
}

/** Format ISO date to human-readable (e.g. "Sunday, January 07") */
function formatVisitDate(isoDate: string): string {
	try {
		const d = new Date(isoDate);
		return d.toLocaleDateString("en-US", {
			weekday: "long",
			month: "long",
			day: "2-digit",
		});
	} catch {
		return "";
	}
}

/**
 * Build a single dynamic history entry DOM element.
 * Matches the visual style of Framer entries with consistent 14px titles.
 */
function buildHistoryEntryElement(entry: HistoryEntry, formattedDate: string): HTMLDivElement {
	const row = document.createElement("div");
	row.className = "wb-history-entry";
	row.dataset.historyId = entry.id;

	// ── Icon (gradient fallback + favicon img overlay) ──────
	const icon = document.createElement("div");
	icon.className = "wb-entry-icon";
	if (entry.thumbnailUrl) {
		const img = document.createElement("img");
		img.className = "wb-entry-icon-img";
		img.src = entry.thumbnailUrl;
		img.alt = "";
		img.loading = "lazy";
		// On error, hide the img so gradient shows through
		img.onerror = () => { img.style.display = "none"; };
		icon.appendChild(img);
	}
	row.appendChild(icon);

	// ── Text column ───────────────────────────────────────────
	const textCol = document.createElement("div");
	textCol.className = "wb-entry-text";

	const titleEl = document.createElement("p");
	titleEl.className = "wb-entry-title";
	titleEl.textContent = entry.title;
	textCol.appendChild(titleEl);

	const urlEl = document.createElement("p");
	urlEl.className = "wb-entry-url";
	urlEl.textContent = entry.url;
	textCol.appendChild(urlEl);

	const dateEl = document.createElement("p");
	dateEl.className = "wb-entry-date";
	dateEl.textContent = formattedDate;
	textCol.appendChild(dateEl);

	row.appendChild(textCol);

	// ── Remove button (two-state: salmon circle → expanded "Remove") ──
	const removeWrapper = document.createElement("div");
	removeWrapper.className = "wb-entry-remove";
	removeWrapper.setAttribute("data-remove-state", "circle");

	// State 1: salmon circle with white minus
	const circleBtn = document.createElement("button");
	circleBtn.className = "wb-remove-circle";
	circleBtn.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="8" fill="rgb(255, 149, 140)"/><path d="M4 8h8" stroke="rgb(249, 250, 251)" stroke-width="2" stroke-linecap="round"/></svg>`;
	circleBtn.setAttribute("aria-label", "Remove entry");
	removeWrapper.appendChild(circleBtn);

	// State 2: expanded remove button with text
	const expandedBtn = document.createElement("button");
	expandedBtn.className = "wb-remove-expanded";
	expandedBtn.textContent = "Remove";
	removeWrapper.appendChild(expandedBtn);

	row.appendChild(removeWrapper);

	return row;
}

// ============================================================================
// Sub-components
// ============================================================================

/** Iframe overlay positioned directly over the Framer content area */
function IframeOverlay() {
	const { url, isLoading, setIsLoading, refreshKey, navigate } = useWebBrowser();
	const iframeRef = useRef<HTMLIFrameElement>(null);

	const handleLoad = useCallback(() => {
		setIsLoading(false);
	}, [setIsLoading]);

	// Listen for link clicks from the proxied page
	useEffect(() => {
		function handleMessage(e: MessageEvent) {
			if (e.source !== iframeRef.current?.contentWindow) return;
			if (e.data?.type !== "wb-navigate" || typeof e.data.url !== "string") return;

			try {
				const destination = new URL(e.data.url);
				if (destination.protocol !== "http:" && destination.protocol !== "https:") return;
				navigate(destination.toString());
			} catch {
				// Ignore malformed or non-web destinations from the sandboxed preview.
			}
		}
		window.addEventListener("message", handleMessage);
		return () => window.removeEventListener("message", handleMessage);
	}, [navigate]);

	return (
		<div className="wb-iframe-container">
			{isLoading && (
				<div className="wb-loading-overlay">
					<div className="wb-loading-spinner" />
				</div>
			)}
			<iframe
				ref={iframeRef}
				key={refreshKey}
				src={getProxyUrl(url)}
				title="Web Browser"
				sandbox="allow-scripts"
				referrerPolicy="no-referrer"
				onLoad={handleLoad}
				style={{ opacity: isLoading ? 0 : 1, transition: "opacity 0.15s ease" }}
			/>
		</div>
	);
}

// ============================================================================
// Main Wrapper
// ============================================================================

export function WebBrowserWrapper() {
	const {
		displayUrl,
		siteName,
		canGoBack,
		canGoForward,
		goBack,
		goForward,
		refresh,
		close,
		copyLink: copyLinkToClipboard,
		openExternal,
		historyEntries,
		navigate,
		removeHistoryEntry,
		clearHistory,
	} = useWebBrowser();

	const wrapperRef = useRef<HTMLDivElement>(null);
	const optionsButtonRef = useRef<HTMLButtonElement>(null);
	const optionsMenuRef = useRef<HTMLDivElement>(null);
	const copyFeedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [showClearConfirm, setShowClearConfirm] = useState(false);
	const [showOptions, setShowOptions] = useState(false);
	const [showCopied, setShowCopied] = useState(false);
	const [browserVariant, setBrowserVariant] = useState<"Variant 1" | "Variant 2">("Variant 1");

	// ── Callbacks for MutationObserver closures ──────────────────────────
	const handleShowClearConfirm = useCallback(() => {
		setShowClearConfirm(true);
	}, []);

	const handleConfirmYes = useCallback(() => {
		clearHistory();
		// Framer component transitions to "Confirmed" variant internally;
		// auto-dismiss the overlay after a short delay
		setTimeout(() => setShowClearConfirm(false), 1200);
	}, [clearHistory]);

	const handleConfirmNo = useCallback(() => {
		setShowClearConfirm(false);
	}, []);

	const closeOptions = useCallback((restoreFocus = false) => {
		setShowOptions(false);
		if (restoreFocus) {
			requestAnimationFrame(() => optionsButtonRef.current?.focus());
		}
	}, []);

	const handleCopyLink = useCallback(async () => {
		await copyLinkToClipboard();
		setShowCopied(true);
		closeOptions(true);
		if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
		copyFeedbackTimerRef.current = setTimeout(() => setShowCopied(false), 1600);
	}, [closeOptions, copyLinkToClipboard]);

	const handleOpenExternal = useCallback(() => {
		closeOptions(true);
		openExternal();
	}, [closeOptions, openExternal]);

	useEffect(() => {
		if (!showOptions) return;

		const firstAction = optionsMenuRef.current?.querySelector<HTMLButtonElement>("button");
		requestAnimationFrame(() => firstAction?.focus());

		function handlePointerDown(event: PointerEvent) {
			const target = event.target as Node;
			if (
				optionsMenuRef.current?.contains(target)
				|| optionsButtonRef.current?.contains(target)
			) return;
			closeOptions();
		}

		function handleKeyDown(event: KeyboardEvent) {
			if (event.key !== "Escape") return;
			event.preventDefault();
			closeOptions(true);
		}

		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [closeOptions, showOptions]);

	useEffect(() => () => {
		if (copyFeedbackTimerRef.current) clearTimeout(copyFeedbackTimerRef.current);
	}, []);

	// ── Refs for latest state (used in MutationObserver callbacks) ──────
	const stateRef = useRef({
		historyEntries, navigate, removeHistoryEntry, clearHistory,
		goBack, goForward, refresh, copyLink: handleCopyLink,
		openExternal: handleOpenExternal, close,
		displayUrl, siteName, canGoBack, canGoForward,
		showClearConfirm: handleShowClearConfirm,
	});
	useEffect(() => {
		stateRef.current = {
			historyEntries, navigate, removeHistoryEntry, clearHistory,
			goBack, goForward, refresh, copyLink: handleCopyLink,
			openExternal: handleOpenExternal, close,
			displayUrl, siteName, canGoBack, canGoForward,
			showClearConfirm: handleShowClearConfirm,
		};
	}, [
		canGoBack,
		canGoForward,
		clearHistory,
		close,
		displayUrl,
		goBack,
		goForward,
		handleCopyLink,
		handleOpenExternal,
		handleShowClearConfirm,
		historyEntries,
		navigate,
		refresh,
		removeHistoryEntry,
		siteName,
	]);

	// ── Wire V1 Framer DOM elements (persistent observer) ────────────────
	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		const wiredActionControls = new WeakSet<Element>();
		const wiredFramerControls = new WeakSet<Element>();
		const wiredVariantTriggers = new WeakSet<Element>();
		let rafId: number | null = null;

		function wireActionControl(
			el: HTMLElement | null,
			label: string,
			handler: (e: Event) => void,
			disabled = false,
		): void {
			if (!el) return;
			el.setAttribute("role", "button");
			el.setAttribute("aria-label", label);
			el.setAttribute("aria-disabled", disabled ? "true" : "false");
			el.tabIndex = disabled ? -1 : 0;

			if (wiredActionControls.has(el)) return;
			wiredActionControls.add(el);

			el.addEventListener("click", (event) => {
				if (el.getAttribute("aria-disabled") === "true") {
					event.preventDefault();
					event.stopPropagation();
					return;
				}
				handler(event);
			}, { capture: true });
			el.addEventListener("keydown", (event) => {
				const keyboardEvent = event as KeyboardEvent;
				if (keyboardEvent.key !== "Enter" && keyboardEvent.key !== " ") return;
				keyboardEvent.preventDefault();
				if (el.getAttribute("aria-disabled") === "true") return;
				handler(event);
			});
		}

		function wireFramerControl(
			el: HTMLElement | null,
			label: string,
			handleKeyboardActivation?: () => void,
		): void {
			if (!el) return;
			el.setAttribute("role", "button");
			el.setAttribute("aria-label", label);
			el.tabIndex = 0;

			if (wiredFramerControls.has(el)) return;
			wiredFramerControls.add(el);
			el.addEventListener("keydown", (event) => {
				const keyboardEvent = event as KeyboardEvent;
				if (keyboardEvent.key !== "Enter" && keyboardEvent.key !== " ") return;
				keyboardEvent.preventDefault();
				if (handleKeyboardActivation) handleKeyboardActivation();
				else el.click();
			});
		}

		function trackHistoryActivation(el: HTMLElement | null): void {
			if (!el || wiredVariantTriggers.has(el)) return;
			wiredVariantTriggers.add(el);
			el.addEventListener("click", () => {
				requestAnimationFrame(() => {
					setBrowserVariant("Variant 2");
				});
			}, { capture: true });
		}

		function trackExitActivation(el: HTMLElement | null, root: Element): void {
			if (!el || wiredVariantTriggers.has(el)) return;
			wiredVariantTriggers.add(el);
			el.addEventListener("click", () => {
				if (!root.classList.contains("framer-v-1o67uvb")) return;
				requestAnimationFrame(() => setBrowserVariant("Variant 1"));
			}, { capture: true });
		}

		function syncV1() {
			const root = wrapper!.querySelector(".framer-EasG9");
			if (!root) return;

			// ── Site info (set once per element creation) ──────────────
			const siteInfo = root.querySelector<HTMLElement>(
				'[data-framer-name="WebBrowserSiteInfo"]',
			);
			if (siteInfo) {
				const s = stateRef.current;
				const urlText = siteInfo.querySelector<HTMLElement>(".framer-i6bup p");
				if (urlText) urlText.textContent = s.displayUrl;
				const nameText = siteInfo.querySelector<HTMLElement>(".framer-vj5dlu p");
				if (nameText) nameText.textContent = s.siteName;
				siteInfo.setAttribute("aria-haspopup", "dialog");
				wireFramerControl(siteInfo, "Page actions");
			}

			// Keep Framer's unsupported menu as an inert layout spacer. The
			// wrapper renders the supported options in a semantic menu instead.
			const generatedOptions = root.querySelector<HTMLElement>(
				'[data-framer-name="WebPageOptionsButton"]',
			);
			if (generatedOptions) {
				generatedOptions.setAttribute("aria-hidden", "true");
				generatedOptions.setAttribute("inert", "");
				generatedOptions.tabIndex = -1;
			}

			const exitButton = root.querySelector<HTMLElement>(
				'[data-framer-name="WebBrowserExit"]',
			);
			wireFramerControl(
				exitButton,
				root.classList.contains("framer-v-1o67uvb") ? "Return to browser" : "Close browser",
				() => {
					if (root.classList.contains("framer-v-1o67uvb")) {
						setBrowserVariant("Variant 1");
					} else {
						stateRef.current.close();
					}
				},
			);
			trackExitActivation(exitButton, root);

			// ── Back button ──────────────────────────────────────────
			const backBtn = root.querySelector<HTMLElement>(
				'[data-framer-name="WebBrowserBack"]',
			);
			wireActionControl(backBtn, "Go back", (e: Event) => {
				e.stopPropagation();
				stateRef.current.goBack();
			}, !stateRef.current.canGoBack);
			if (backBtn) {
				backBtn.setAttribute(
					"data-wb-disabled",
					stateRef.current.canGoBack ? "false" : "true",
				);
			}

			// ── Forward button ───────────────────────────────────────
			const forwardBtn = root.querySelector<HTMLElement>(
				'[data-framer-name="WebBrowserForward"]',
			);
			wireActionControl(forwardBtn, "Go forward", (e: Event) => {
				e.stopPropagation();
				stateRef.current.goForward();
			}, !stateRef.current.canGoForward);
			if (forwardBtn) {
				forwardBtn.setAttribute(
					"data-wb-disabled",
					stateRef.current.canGoForward ? "false" : "true",
				);
			}

			// ── Refresh button ───────────────────────────────────────
			const refreshBtn = root.querySelector<HTMLElement>(
				'[data-framer-name="WebBrowserRefresh"]',
			);
			wireActionControl(refreshBtn, "Refresh page", (e: Event) => {
				e.stopPropagation();
				stateRef.current.refresh();
			});

			const historyButton = root.querySelector<HTMLElement>(
				'[data-framer-name="WebBrowserHistory"]',
			);
			wireFramerControl(
				historyButton,
				"View browsing history",
				() => setBrowserVariant("Variant 2"),
			);
			trackHistoryActivation(historyButton);
		}

		// ── Popup observer (Copy Link / Open External) ─────────────────
		const popupObserver = new MutationObserver(() => {
			const popups = document.querySelectorAll<HTMLElement>(
				'[role="dialog"]',
			);
			popups.forEach((popup) => {
				if (popup.getAttribute("data-wb-wired")) return;

				const rows = Array.from(popup.querySelectorAll<HTMLElement>("div[style]"));
				const copyRow = rows.find((row) => row.textContent?.trim() === STRINGS.copyLink);
				const externalRow = rows.find((row) => row.textContent?.trim() === STRINGS.openExternal);
				if (!copyRow || !externalRow) return;

				popup.setAttribute("data-wb-wired", "true");
				popup.setAttribute("aria-label", "Page actions");
				wireActionControl(copyRow, STRINGS.copyLink, (event) => {
					event.stopPropagation();
					event.preventDefault();
					void stateRef.current.copyLink();
				});
				wireActionControl(externalRow, STRINGS.openExternal, (event) => {
					event.stopPropagation();
					event.preventDefault();
					stateRef.current.openExternal();
				});
			});
		});
		popupObserver.observe(document.body, { childList: true, subtree: true });

		// ── Main DOM observer (persistent — re-wires after variant switches) ─
		const observer = new MutationObserver(() => {
			if (rafId) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(syncV1);
		});
		observer.observe(wrapper, {
			attributes: true,
			attributeFilter: ["class"],
			childList: true,
			subtree: true,
		});

		// Initial sync
		requestAnimationFrame(syncV1);

		return () => {
			observer.disconnect();
			popupObserver.disconnect();
			if (rafId) cancelAnimationFrame(rafId);
		};
	}, []);

	// ── Update site info text when URL changes ────────────────────────────
	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		const root = wrapper.querySelector(".framer-EasG9");
		if (!root) return;

		const siteInfo = root.querySelector<HTMLElement>(
			'[data-framer-name="WebBrowserSiteInfo"]',
		);
		if (!siteInfo) return;

		const urlText = siteInfo.querySelector<HTMLElement>(".framer-i6bup p");
		if (urlText) urlText.textContent = displayUrl;

		const nameText = siteInfo.querySelector<HTMLElement>(".framer-vj5dlu p");
		if (nameText) nameText.textContent = siteName;
	}, [displayUrl, siteName]);

	// ── Update arrow disabled state ───────────────────────────────────────
	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		const root = wrapper.querySelector(".framer-EasG9");
		if (!root) return;

		const backBtn = root.querySelector<HTMLElement>(
			'[data-framer-name="WebBrowserBack"]',
		);
		const forwardBtn = root.querySelector<HTMLElement>(
			'[data-framer-name="WebBrowserForward"]',
		);

		if (backBtn) {
			backBtn.setAttribute("data-wb-disabled", canGoBack ? "false" : "true");
			backBtn.setAttribute("aria-disabled", canGoBack ? "false" : "true");
			backBtn.tabIndex = canGoBack ? 0 : -1;
		}
		if (forwardBtn) {
			forwardBtn.setAttribute(
				"data-wb-disabled",
				canGoForward ? "false" : "true",
			);
			forwardBtn.setAttribute("aria-disabled", canGoForward ? "false" : "true");
			forwardBtn.tabIndex = canGoForward ? 0 : -1;
		}
	}, [canGoBack, canGoForward]);

	// ── V2: Inject dynamic history entries when variant 2 activates ──────
	useEffect(() => {
		const wrapper = wrapperRef.current;
		if (!wrapper) return;

		let v2Injected = false;
		let v2Cleanup: (() => void)[] = [];
		let rafId: number | null = null;

		function cleanV2() {
			v2Cleanup.forEach((fn) => fn());
			v2Cleanup = [];
		}

		/** Check if we're in V2 by looking for the V2 variant class */
		function isV2Active(): boolean {
			const root = wrapper!.querySelector(".framer-EasG9");
			return !!root?.classList.contains("framer-v-1o67uvb");
		}

		function injectEntries() {
			const root = wrapper!.querySelector(".framer-EasG9");
			if (!root) return;

			if (!isV2Active()) {
				if (v2Injected) {
					cleanV2();
					v2Injected = false;
				}
				return;
			}

			// Already injected for this V2 activation — don't re-inject
			if (v2Injected) return;

			const historyList = root.querySelector<HTMLElement>(".framer-ln8zgq");
			if (!historyList) return;

			// Temporarily disconnect observer to prevent re-triggering from our DOM changes
			observer.disconnect();

			// Remove previously injected entries
			historyList.querySelectorAll(".wb-history-entry, .wb-empty-history").forEach((el) => el.remove());
			cleanV2();
			v2Injected = true;

			const entries = stateRef.current.historyEntries;

			if (entries.length === 0) {
				const emptyDiv = document.createElement("div");
				emptyDiv.className = "wb-empty-history";
				emptyDiv.textContent = STRINGS.noHistory;
				historyList.appendChild(emptyDiv);
				// Re-connect observer
				observer.observe(wrapper!, {
					attributes: true,
					attributeFilter: ["class"],
					childList: true,
					subtree: true,
				});
				return;
			}

			// Inject one entry per history item (no limit, all entries)
			entries.forEach((entry, i) => {
				const el = buildHistoryEntryElement(entry, formatVisitDate(entry.visitedAt));
				historyList.appendChild(el);

				// Click on row → navigate to that URL
				const rowHandler = (e: Event) => {
					// Don't navigate if clicking remove area
					if ((e.target as HTMLElement).closest(".wb-entry-remove")) return;
					// If any remove button is expanded, collapse it instead of navigating
					const anyExpanded = historyList!.querySelector('[data-remove-state="expanded"]');
					if (anyExpanded) {
						anyExpanded.setAttribute("data-remove-state", "circle");
						e.stopPropagation();
						return;
					}
					e.stopPropagation();
					const current = stateRef.current.historyEntries[i];
					if (!current) return;
					const exitBtn = root.querySelector<HTMLElement>(
						'[data-framer-name="WebBrowserExit"]',
					);
					exitBtn?.click();
					setTimeout(() => stateRef.current.navigate(current.url), 100);
				};
				el.addEventListener("click", rowHandler);
				v2Cleanup.push(() => el.removeEventListener("click", rowHandler));

				// Remove circle button → expand to "Remove"
				const circleBtn = el.querySelector<HTMLElement>(".wb-remove-circle");
				if (circleBtn) {
					const circleHandler = (e: Event) => {
						e.stopPropagation();
						e.preventDefault();
						// Collapse any other expanded remove buttons first
						historyList!.querySelectorAll('[data-remove-state="expanded"]').forEach((other) => {
							other.setAttribute("data-remove-state", "circle");
						});
						const wrapper = circleBtn.closest(".wb-entry-remove");
						if (wrapper) wrapper.setAttribute("data-remove-state", "expanded");
					};
					circleBtn.addEventListener("click", circleHandler);
					v2Cleanup.push(() => circleBtn.removeEventListener("click", circleHandler));
				}

				// Expanded "Remove" button → actually remove the entry
				const expandedBtn = el.querySelector<HTMLElement>(".wb-remove-expanded");
				if (expandedBtn) {
					const expandedHandler = (e: Event) => {
						e.stopPropagation();
						e.preventDefault();
						const current = stateRef.current.historyEntries[i];
						if (current) stateRef.current.removeHistoryEntry(current.id);
					};
					expandedBtn.addEventListener("click", expandedHandler);
					v2Cleanup.push(() => expandedBtn.removeEventListener("click", expandedHandler));
				}
			});

			// ── ClearAll button ──────────────────────────────────────
			const clearAllBtn = root.querySelector<HTMLElement>(".framer-1tc2gox");
			if (clearAllBtn) {
				const handler = (e: Event) => {
					e.stopPropagation();
					stateRef.current.showClearConfirm();
				};
				clearAllBtn.addEventListener("click", handler, { capture: true });
				v2Cleanup.push(() => clearAllBtn.removeEventListener("click", handler, { capture: true }));
			}

			// Re-connect observer now that our DOM changes are done
			observer.observe(wrapper!, {
				attributes: true,
				attributeFilter: ["class"],
				childList: true,
				subtree: true,
			});
		}

		// MutationObserver detects V2 activation / deactivation
		const observer = new MutationObserver(() => {
			if (rafId) cancelAnimationFrame(rafId);
			rafId = requestAnimationFrame(injectEntries);
		});
		observer.observe(wrapper, {
			attributes: true,
			attributeFilter: ["class"],
			childList: true,
			subtree: true,
		});

		// Try immediately
		requestAnimationFrame(injectEntries);

		return () => {
			cleanV2();
			observer.disconnect();
			if (rafId) cancelAnimationFrame(rafId);
		};
	}, [browserVariant, historyEntries]);

	// ── Close handler for the Framer click prop ───────────────────────────
	const handleClose = useCallback(() => {
		close();
	}, [close]);

	return (
		<div
			ref={wrapperRef}
			className="wb-browser-shell"
			style={{ width: 393, height: 851, position: "relative" }}
		>
			<FramerWebBrowser
				variant={browserVariant}
				locale=""
				click={handleClose}
				style={{ width: "100%", height: "100%" }}
			/>

			<button
				ref={optionsButtonRef}
				type="button"
				className="wb-options-trigger"
				aria-label="Browser options"
				aria-haspopup="menu"
				aria-expanded={showOptions}
				aria-controls="wb-options-menu"
				onClick={() => setShowOptions((current) => !current)}
			>
				<svg aria-hidden="true" viewBox="0 0 24 24" width="24" height="24">
					<circle cx="5" cy="12" r="2" />
					<circle cx="12" cy="12" r="2" />
					<circle cx="19" cy="12" r="2" />
				</svg>
			</button>

			{showOptions && (
				<div
					ref={optionsMenuRef}
					id="wb-options-menu"
					className="wb-options-menu"
					role="menu"
					aria-label="Browser options"
					onKeyDown={(event) => {
						if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
						event.preventDefault();
						const actions = Array.from(
							event.currentTarget.querySelectorAll<HTMLButtonElement>("button"),
						);
						const currentIndex = actions.indexOf(document.activeElement as HTMLButtonElement);
						if (event.key === "Home") actions[0]?.focus();
						else if (event.key === "End") actions.at(-1)?.focus();
						else if (event.key === "ArrowDown") actions[(currentIndex + 1) % actions.length]?.focus();
						else actions[(currentIndex - 1 + actions.length) % actions.length]?.focus();
					}}
				>
					<button type="button" role="menuitem" onClick={() => void handleCopyLink()}>
						<svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20">
							<path d="M9 8h9a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2Z" />
							<path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h2" />
						</svg>
						<span>Copy link</span>
					</button>
					<button type="button" role="menuitem" onClick={handleOpenExternal}>
						<svg aria-hidden="true" viewBox="0 0 24 24" width="20" height="20">
							<path d="M14 3h7v7" />
							<path d="M10 14 21 3" />
							<path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
						</svg>
						<span>Open in external browser</span>
					</button>
				</div>
			)}

			{showCopied && (
				<div className="wb-copy-toast" role="status" aria-live="polite">
					{STRINGS.copied}
				</div>
			)}

			{/* Iframe overlay positioned over the content area (between header and bottom nav) */}
			<IframeOverlay />

			{/* Clear-all confirmation overlay using Framer ConfirmationRequest */}
			{showClearConfirm && (
				<div className="wb-confirm-overlay" onClick={handleConfirmNo}>
					<div
						onClick={(e) => e.stopPropagation()}
						onClickCapture={(e) => {
							const target = e.target as HTMLElement;
							if (target.closest('[data-framer-name="ConfirmationRequestYES"]')) {
								handleConfirmYes();
							}
						}}
					>
						<ConfirmationRequest
							question="Clear all browsing history?"
							message="History Cleared"
							exitClick={handleConfirmNo}
							cONFIRMATIONTEXT="YES"
							dECLINETEXT="NO"
						/>
					</div>
				</div>
			)}
		</div>
	);
}

export default WebBrowserWrapper;

